<template>
    <section name="file-upload" class="gap-sm flex w-full min-w-0 flex-col">
        <div
            v-if="isViewportDragActive"
            class="pointer-events-none fixed inset-[4px] z-50 flex items-center justify-center rounded-xl border-2 border-dashed border-(--border-focus) bg-(--surface-highlight)/70 px-6"
            data-file-upload-overlay
        />

        <div
            tabindex="0"
            class="input-file"
            data-file-upload-root
            :class="{ error: error, 'drag-active': isDragActive || isViewportDragActive }"
            @keydown.enter="triggerFileUpload"
            @click="triggerFileUpload"
            @dragenter.prevent="handleDragEnter"
            @dragover.prevent="handleDragOver"
            @dragleave.prevent="handleDragLeave"
            @drop.prevent="handleDrop"
        >
            <i class="i-upload-primary h-12 w-12" :class="{ 'i-upload-error': error }" />
            <p class="text-center">Paste a screenshot or file here, or click to upload</p>
            <input
                ref="fileInput"
                type="file"
                class="hidden"
                :multiple="props.maxFiles !== 1"
                @change="handleFileUpload"
            />
        </div>

        <div
            v-if="uploadedFiles.length > 0"
            tabindex="0"
            class="flex w-full items-center justify-between"
            @keydown.enter="toggleUploadedFiles"
            @click="toggleUploadedFiles"
        >
            <p class="">Uploaded files</p>
            <div class="rotate-wrapper flex">
                <i class="i-small-arrow-down h-6 w-6" :class="{ flipped: showUploadedFiles }" />
            </div>
        </div>

        <ul v-if="uploadedFiles.length > 0" id="files" class="uploaded-files">
            <template v-if="showUploadedFiles">
                <li v-for="(file, index) in uploadedFiles" :key="index" class="uploaded-file">
                    <div
                        class="flex h-12 w-12 shrink-0 items-center justify-center"
                        :class="{ 'rounded-sm bg-(--tag-primary)': !isImage(file) }"
                    >
                        <img
                            v-if="isImage(file)"
                            :src="getFilePreview(file)"
                            alt="File Preview"
                            class="h-full w-full rounded-sm object-cover"
                        />
                        <i v-else class="i-file h-4 w-4" />
                    </div>
                    <div class="gap-xs flex min-w-0 flex-1 flex-col">
                        <p class="h-5 max-w-full overflow-hidden text-ellipsis whitespace-nowrap">
                            {{ file.name }}
                        </p>
                        <span class="text-(--text-muted)">{{ Math.round(file.size / 1024) }} KB</span>
                    </div>
                    <button
                        type="button"
                        class="uploaded-file__remove mr-xs ml-auto"
                        title="Remove attachment"
                        aria-label="Remove attachment"
                        @click="removeFile(index)"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            class="h-5 w-5"
                            aria-hidden="true"
                        >
                            <path d="M18 6 6 18" />
                            <path d="m6 6 12 12" />
                        </svg>
                    </button>
                </li>
            </template>
        </ul>
    </section>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, type PropType } from 'vue';
import { isImage, getFilePreview } from '@/stores/helpers';
import { useOrganizationStore } from '@/stores/organization';
import { isClipboardFileInputChange } from '@/lib/file-input-paste';

const fileInput = ref<HTMLInputElement | null>(null);
const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB in bytes

const organizationStore = useOrganizationStore();
const showUploadedFiles = ref(true);
const isDragActive = ref(false);
const isViewportDragActive = ref(false);
const dragDepth = ref(0);
const emit = defineEmits(['upload', 'remove']);

const props = defineProps({
    uploadedFiles: {
        type: Array as PropType<File[]>,
        default: () => [],
    },
    error: {
        type: Boolean,
        default: false,
    },
    maxFiles: {
        type: Number,
        default: 5,
    },
});

const triggerFileUpload = () => {
    fileInput.value?.click();
};

function addFiles(files: File[] | FileList) {
    const filesArray = Array.from(files);
    const totalFiles = props.uploadedFiles.length + filesArray.length;

    if (filesArray.length === 0) {
        return false;
    }

    if (totalFiles > props.maxFiles) {
        organizationStore.setToastMessage(`You can only upload a maximum of ${props.maxFiles} files`, 'warning');
        return false;
    }

    const oversizedFiles = filesArray.filter((file) => file.size > MAX_FILE_SIZE);
    const undersizedFiles = filesArray.filter((file) => file.size < 1);

    if (oversizedFiles.length > 0) {
        organizationStore.setToastMessage('Each file must be smaller than 200MB', 'warning');
        return false;
    }

    if (undersizedFiles.length > 0) {
        organizationStore.setToastMessage('You cannot upload empty attachments', 'warning');
        return false;
    }

    emit('upload', [...props.uploadedFiles, ...filesArray]);
    return true;
}

function hasDraggedFiles(event: DragEvent) {
    return Array.from(event.dataTransfer?.types ?? []).includes('Files');
}

function clearDragState() {
    isDragActive.value = false;
    dragDepth.value = 0;
}

function clearViewportDragState() {
    isViewportDragActive.value = false;
}

function clearAllDragState() {
    clearDragState();
    clearViewportDragState();
}

function shouldUseViewportDropTarget() {
    if (typeof document === 'undefined') {
        return false;
    }

    return document.querySelectorAll('[data-file-upload-root]').length === 1;
}

function isPointerOutsideViewport(event: DragEvent | MouseEvent) {
    return (
        event.clientX <= 0 ||
        event.clientY <= 0 ||
        event.clientX >= window.innerWidth ||
        event.clientY >= window.innerHeight
    );
}

function isWithinUploadRoot(target: EventTarget | null) {
    return target instanceof Element && target.closest('[data-file-upload-root]') !== null;
}

const handleFileUpload = (event: Event) => {
    const target = event.target as HTMLInputElement;
    const resetInput = () => {
        target.value = '';
    };

    if (!target.files) {
        return;
    }

    const filesArray = Array.from(target.files);
    const filesAdded = addFiles(filesArray);

    if (filesAdded && isClipboardFileInputChange(event) && filesArray.some((file) => file.type.startsWith('image/'))) {
        organizationStore.setToastMessage('Image uploaded', 'check');
    }

    resetInput();
};

function handleDragEnter(event: DragEvent) {
    if (!hasDraggedFiles(event)) {
        return;
    }

    dragDepth.value += 1;
    isDragActive.value = true;
}

function handleDragOver(event: DragEvent) {
    if (!hasDraggedFiles(event)) {
        return;
    }

    if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
    }

    isDragActive.value = true;
}

function handleDragLeave() {
    if (!isDragActive.value) {
        return;
    }

    dragDepth.value = Math.max(dragDepth.value - 1, 0);
    if (dragDepth.value === 0) {
        isDragActive.value = false;
    }
}

function handleDrop(event: DragEvent) {
    const files = event.dataTransfer?.files;

    clearAllDragState();

    if (!files || files.length === 0) {
        return;
    }

    addFiles(files);
}

function handleWindowDragEnter(event: DragEvent) {
    if (!shouldUseViewportDropTarget() || !hasDraggedFiles(event)) {
        return;
    }

    isViewportDragActive.value = true;
}

function handleWindowDragOver(event: DragEvent) {
    if (!shouldUseViewportDropTarget() || !hasDraggedFiles(event)) {
        return;
    }

    event.preventDefault();

    if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
    }

    isViewportDragActive.value = true;
}

function handleWindowDragLeave(event: DragEvent) {
    if (!shouldUseViewportDropTarget() || !isViewportDragActive.value) {
        return;
    }

    if (event.relatedTarget instanceof Node && document.documentElement.contains(event.relatedTarget)) {
        return;
    }

    if (!event.relatedTarget || isPointerOutsideViewport(event)) {
        clearAllDragState();
    }
}

function handleWindowDrop(event: DragEvent) {
    if (!hasDraggedFiles(event)) {
        return;
    }

    const files = event.dataTransfer?.files;
    const droppedOnUploadRoot = isWithinUploadRoot(event.target);

    clearAllDragState();

    if (!shouldUseViewportDropTarget() || droppedOnUploadRoot || !files || files.length === 0) {
        return;
    }

    event.preventDefault();
    addFiles(files);
}

function handleWindowMouseOut(event: MouseEvent) {
    if (!(isDragActive.value || isViewportDragActive.value)) {
        return;
    }

    if (event.relatedTarget instanceof Node && document.documentElement.contains(event.relatedTarget)) {
        return;
    }

    if (!event.relatedTarget || isPointerOutsideViewport(event)) {
        clearAllDragState();
    }
}

function handleWindowBlur() {
    clearAllDragState();
}

function handleWindowDragEnd() {
    clearAllDragState();
}

function handleDocumentVisibilityChange() {
    if (document.visibilityState === 'hidden') {
        clearAllDragState();
    }
}

onMounted(() => {
    window.addEventListener('dragenter', handleWindowDragEnter);
    window.addEventListener('dragover', handleWindowDragOver);
    window.addEventListener('dragleave', handleWindowDragLeave);
    window.addEventListener('drop', handleWindowDrop);
    window.addEventListener('mouseout', handleWindowMouseOut);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('dragend', handleWindowDragEnd);
    document.addEventListener('visibilitychange', handleDocumentVisibilityChange);
});

onBeforeUnmount(() => {
    window.removeEventListener('dragenter', handleWindowDragEnter);
    window.removeEventListener('dragover', handleWindowDragOver);
    window.removeEventListener('dragleave', handleWindowDragLeave);
    window.removeEventListener('drop', handleWindowDrop);
    window.removeEventListener('mouseout', handleWindowMouseOut);
    window.removeEventListener('blur', handleWindowBlur);
    window.removeEventListener('dragend', handleWindowDragEnd);
    document.removeEventListener('visibilitychange', handleDocumentVisibilityChange);
});

function removeFile(index: number) {
    emit('remove', index);
}

function toggleUploadedFiles() {
    showUploadedFiles.value = !showUploadedFiles.value;
}
</script>

<style scoped>
@reference '@/assets/styles/default.css';
.input-file {
    @apply p-lg gap-sm flex w-full flex-col items-center rounded-md border-2 border-dotted border-(--border-subtle) hover:border-(--border-focus) hover:bg-(--surface-highlight);
}

.error {
    @apply border-red-400;
}

.drag-active {
    @apply border-(--border-focus) bg-(--surface-highlight) ring-1 ring-(--border-focus);
}

.uploaded-files {
    @apply gap-sm flex w-full flex-col;
}

.uploaded-file {
    @apply gap-sm p-xs flex w-full items-center rounded-md;
    box-shadow: 0 0 0 1px var(--color-gray-100);

    &:hover {
        outline: auto 1px var(--color-blue-500);
    }

    &:focus-within {
        outline: auto 2px var(--color-blue-100);
    }
}

.uploaded-file__remove {
    @apply inline-flex items-center justify-center rounded-sm text-(--text-muted) transition-colors hover:text-primary focus:ring-2 focus:ring-blue-100 focus:outline-none;
}
</style>
